import { Text, View } from 'native-base';
import React, { ReactNode, useMemo } from 'react';

import HTML, { HTMLRendererProps } from 'app/components/HTML';
import useToggle from 'app/hooks/useToggle';
import { useI18n } from 'app/providers/I18nProvider';
import { tID } from 'app/services/TestHelper';

export type HTMLCollapsibleProps = {
  children?: ReactNode;
  html: string;
  numberOfLines: number;
  initialOpen?: boolean;
  onExpand?: () => void;
  collapsedContentTestID?: string;
  expandedContentTestID?: string;
  showMoreTestID?: string;
  showLessTestID?: string;
} & HTMLRendererProps;

const APPROX_CHARS_PER_LINE = 50;

export default function HTMLCollapsible({
  html,
  initialOpen,
  numberOfLines,
  onExpand,
  collapsedContentTestID,
  expandedContentTestID,
  showMoreTestID,
  showLessTestID,
  ...props
}: HTMLCollapsibleProps) {
  const { t } = useI18n();
  const [expanded, toggle] = useToggle(initialOpen);
  const threshold = numberOfLines * APPROX_CHARS_PER_LINE;
  const isCollapsible = html.length > threshold;
  const truncatedHtml = useMemo(() => {
    if (!isCollapsible || expanded) return html;
    // a threshold of 0 hides the text entirely until expanded
    return threshold ? `${html.substring(0, threshold)}...` : '';
  }, [expanded, html, isCollapsible, threshold]);

  const handleToggle = () => {
    if (!expanded && onExpand) onExpand();
    toggle();
  };
  const contentTestID = expanded ? expandedContentTestID : collapsedContentTestID;
  const toggleTestID = expanded ? showLessTestID : showMoreTestID;

  return (
    <View testID={tID('HTMLCollapsible')}>
      <View testID={contentTestID ? tID(contentTestID) : undefined}>
        <HTML {...props} defaultTextProps={props.defaultTextProps} html={truncatedHtml} />
      </View>
      {isCollapsible && (
        <Text
          color={props.color}
          pt="2"
          testID={tID(toggleTestID || 'HTMLCollapsibleToggle')}
          underline
          onPress={handleToggle}
        >
          {expanded ? t('gen_toggle_show_less') : t('gen_toggle_show_more')}
        </Text>
      )}
    </View>
  );
}
